import React, { useState, useEffect, useMemo } from "react";
import AdminKeywordTemplates from "@/pages/admin/AdminKeywordTemplates";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getAllSEOSettings, updateSEOSettings } from "@/lib/api";
import { SeoHealthCheck } from "@/components/seo/SeoHealthCheck";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Globe, Save, Loader2, CheckCircle, AlertCircle,
  Share2, Code, Tag, Eye, Twitter, FileJson, ChevronDown,
  TrendingUp, XCircle, Info, BarChart3,
} from "lucide-react";

const seoPages = [
  { slug: "global", name: "Global Defaults", description: "Default SEO applied to all pages, overridden by page-specific settings" },
  { slug: "home", name: "Home Page", description: "Main landing page SEO" },
  { slug: "about", name: "About Page", description: "About page SEO" },
  { slug: "services", name: "Services Page", description: "Services page SEO" },
  { slug: "locations", name: "Locations Page", description: "Locations page SEO" },
  { slug: "contact", name: "Contact Page", description: "Contact page SEO" },
  { slug: "become-dealer", name: "Become a Dealer", description: "Dealer application page SEO" },
  { slug: "our-process", name: "Our Process", description: "Process page SEO" },
  { slug: "faqs", name: "FAQs Page", description: "Frequently Asked Questions page SEO" },
  { slug: "careers", name: "Careers Page", description: "Careers and job listings page SEO" },
  { slug: "totalwirelessmasteragent", name: "TWMA Page", description: "Total Wireless Master Agent landing page SEO" },
  { slug: "open-a-store", name: "Open a Store", description: "/open-a-store landing page SEO" },
  { slug: "state-template", name: "State Pages (Template)", description: "Template applied to every /<state> page. Use {State} anywhere in the text — it's replaced with the actual state name per page (e.g. {State} → Ohio)." },
];

// Canonical company info used in the schema across all pages (per user spec).
const COMPANY_SCHEMA_BASE = {
  "name": "Prime Media Wireless",
  "url": "https://primemediawireless.com",
  "telephone": "(248) 590-0000",
  "email": "info@primemediawireless.com",
  "description": "Partner with Prime Media Wireless, a Trusted Premier Total Wireless Master Agent backed by the Verizon 5G Network. We help you launch and grow no-contract/prepaid wireless stores with expert guidance, 30+ years of experience, and nationwide dealer support.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "31455 Northwestern Hwy Ste C",
    "addressLocality": "Farmington Hills",
    "addressRegion": "MI",
    "postalCode": "48334",
    "addressCountry": "US",
  },
};

// Hardcoded fallback lists (overridden at runtime by admin-editable lists from
// /api/seo-ops/localbusiness-lists whenever the Load Default button is clicked).
const DEFAULT_KNOWS_ABOUT_FALLBACK = [
  "Total Wireless", "Verizon Value", "Master Agent",
  "Total Wireless Master Agent", "Total Wireless Dealer",
];
const DEFAULT_OFFERS_FALLBACK = [
  "Dealer Program with incentive details",
  "Total Wireless Device Sales",
];

// Kept for backwards-compat — some tests still reference it.
const DEFAULT_LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  ...COMPANY_SCHEMA_BASE,
  "knowsAbout": DEFAULT_KNOWS_ABOUT_FALLBACK,
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Total Wireless Products & Dealer Program",
    "itemListElement": DEFAULT_OFFERS_FALLBACK.map((n) => ({ "@type": "Offer", "name": n })),
  },
};

// ─── Old default schema kept for reference ─────────────────────
// This was the previous long hardcoded list. Admin-editable lists replace it.
// eslint-disable-next-line no-unused-vars
const LEGACY_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Prime Media Wireless",
  "url": "https://primemediawireless.com",
  "telephone": "(248) 590-0000",
  "email": "info@primemediawireless.com",
  "description": "Prime Media Wireless is a Trusted Premier Total Wireless Master Agent — Covered by Verizon.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "31455 Northwestern Hwy Ste C",
    "addressLocality": "Farmington Hills",
    "addressRegion": "MI",
    "postalCode": "48334",
    "addressCountry": "US"
  },
  "knowsAbout": [
    "Total Wireless",
    "Verizon Value",
    "Master Agent",
    "Total Wireless Master Agent",
    "Total Wireless Dealer",
    "Total Wireless Master Agent and Dealer",
    "Total Wireless Exclusive Retailer",
    "trusted nationwide wireless solutions provider",
    "specializing in prepaid wireless",
    "retail store operations",
    "we empower dealers and agents with top-quality products",
    "total wireless near me",
    "total wireless store near me",
    "open a total wireless store",
    "how to open a total wireless store",
    "How do I become an authorized dealer for Total Wireless",
    "What are the requirements for the Total Wireless Exclusive Retailer Program",
    "How can I apply to own a Total Wireless store",
    "Where can I find the Total Wireless retailer application form",
    "I want to know the steps to open a Total Wireless store in my state",
    "What are the financial requirements for opening a no-contract/prepaid wireless store",
    "How do I partner with Total Wireless to sell their devices and plans",
    "Nationwide Opportunities to own a wireless business",
    "Nationwide Opportunities to operate a wireless business",
    "Nationwide Opportunities to grow a wireless business"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Total Wireless Products & Dealer Program",
    "itemListElement": [
      { "@type": "Offer", "name": "Dealer Program with incentive details" },
      { "@type": "Offer", "name": "Exclusive Retail Opportunities" },
      { "@type": "Offer", "name": "New Total Wireless Store Launch" },
      { "@type": "Offer", "name": "Total Wireless Device Sales" },
      { "@type": "Offer", "name": "Apple iPhone" },
      { "@type": "Offer", "name": "Apple iPad" },
      { "@type": "Offer", "name": "Samsung Galaxy" },
      { "@type": "Offer", "name": "Google Pixel" },
      { "@type": "Offer", "name": "Motorola Moto G" },
      { "@type": "Offer", "name": "Motorola Edge" },
      { "@type": "Offer", "name": "Motorola Razr" },
      { "@type": "Offer", "name": "TCL Devices" },
      { "@type": "Offer", "name": "Blu Devices" },
      { "@type": "Offer", "name": "Phone" },
      { "@type": "Offer", "name": "Tablet" },
      { "@type": "Offer", "name": "Hotspot" },
      { "@type": "Offer", "name": "Home Internet" },
      { "@type": "Offer", "name": "Bring your own phone" },
      { "@type": "Offer", "name": "Bring your own device" },
      { "@type": "Offer", "name": "Bring your own tablet" },
      { "@type": "Offer", "name": "Phone Repair" },
      { "@type": "Offer", "name": "Tablet Repair" },
      { "@type": "Offer", "name": "Computer Repair" },
      { "@type": "Offer", "name": "Game Console Repair" },
      { "@type": "Offer", "name": "Total Wireless Bill Pay" },
      { "@type": "Offer", "name": "Trade-in my phone" },
      { "@type": "Offer", "name": "Trade-in program" },
      { "@type": "Offer", "name": "Family Plans" },
      { "@type": "Offer", "name": "Unlimited Plans" },
      { "@type": "Offer", "name": "Accessories" },
      { "@type": "Offer", "name": "Total Wireless Account Support" },
      { "@type": "Offer", "name": "Total Wireless Home Internet Router" },
      { "@type": "Offer", "name": "Total Wireless Home Internet" },
      { "@type": "Offer", "name": "CPO Devices" },
      { "@type": "Offer", "name": "Total Wireless Protect" },
      { "@type": "Offer", "name": "Device Financing" },
      { "@type": "Offer", "name": "Phone Financing" },
      { "@type": "Offer", "name": "Glow Financing" },
      { "@type": "Offer", "name": "Total Wireless Phone Financing" },
      { "@type": "Offer", "name": "Glow Financial Services" },
      { "@type": "Offer", "name": "Glow Services" },
      { "@type": "Offer", "name": "Total Wireless Edge" },
      { "@type": "Offer", "name": "24 monthly installments with no down payment required" }
    ]
  }
};

const defaultSEO = {
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  og_title: "",
  og_description: "",
  og_image: "",
  og_type: "website",
  og_url: "",
  canonical_url: "",
  robots: "index, follow",
  twitter_card: "summary_large_image",
  twitter_title: "",
  twitter_description: "",
  twitter_image: "",
  schema_json: "",
  competitor_keywords: "",
  site_name: "",
};

// ─── SEO Score Calculation ──────────────────────────────────────
function calculateSeoScore(pageSettings, globalSettings = null, isGlobal = false) {
  // Merge global + page-specific (page overrides non-empty values)
  const g = globalSettings || {};
  const p = pageSettings || {};
  const data = {};
  for (const key of Object.keys(defaultSEO)) {
    data[key] = (p[key] && p[key].trim?.() ? p[key] : g[key]) || "";
  }
  // Mirrors what the site serves: OG/Twitter copy meta tags, keywords are global-only, schema is automatic
  data.og_title = data.twitter_title = data.meta_title;
  data.og_description = data.twitter_description = data.meta_description;
  data.twitter_image = data.og_image;
  if (!isGlobal) { data.meta_keywords = g.meta_keywords || ""; data.competitor_keywords = g.competitor_keywords || ""; }
  data.schema_json = JSON.stringify({ "@context": "https://schema.org", "@graph": [{ "@type": "LocalBusiness", name: "Prime Media Wireless" }, { "@type": "WebPage" }] });

  let score = 0;
  const checks = [];

  // 1. Meta Title (15 pts: 10 present + 5 optimal length)
  const title = data.meta_title || "";
  if (title) {
    score += 10;
    checks.push({ label: "Meta title present", status: "pass", category: "meta", pts: 10 });
    if (title.length >= 30 && title.length <= 65) {
      score += 5;
      checks.push({ label: "Title length optimal (30-65 chars)", status: "pass", category: "meta", pts: 5 });
    } else {
      checks.push({ label: `Title length: ${title.length} chars (ideal: 30-65)`, status: "warn", category: "meta", pts: 5 });
    }
  } else {
    checks.push({ label: "Meta title missing", status: "fail", category: "meta", pts: 15 });
  }

  // 2. Meta Description (15 pts: 10 present + 5 optimal length)
  const desc = data.meta_description || "";
  if (desc) {
    score += 10;
    checks.push({ label: "Meta description present", status: "pass", category: "meta", pts: 10 });
    if (desc.length >= 120 && desc.length <= 160) {
      score += 5;
      checks.push({ label: "Description length optimal (120-160 chars)", status: "pass", category: "meta", pts: 5 });
    } else {
      checks.push({ label: `Description length: ${desc.length} chars (ideal: 120-160)`, status: "warn", category: "meta", pts: 5 });
    }
  } else {
    checks.push({ label: "Meta description missing", status: "fail", category: "meta", pts: 15 });
  }

  // 3. Keywords (8 pts)
  if (data.meta_keywords || data.competitor_keywords) {
    score += 8;
    checks.push({ label: "Keywords present", status: "pass", category: "keywords", pts: 8 });
  } else {
    checks.push({ label: "No keywords set", status: "fail", category: "keywords", pts: 8 });
  }

  // 4. Open Graph (20 pts: title 7, desc 7, image 6)
  if (data.og_title) {
    score += 7;
    checks.push({ label: "OG title present", status: "pass", category: "opengraph", pts: 7 });
  } else {
    checks.push({ label: "OG title missing", status: "fail", category: "opengraph", pts: 7 });
  }
  if (data.og_description) {
    score += 7;
    checks.push({ label: "OG description present", status: "pass", category: "opengraph", pts: 7 });
  } else {
    checks.push({ label: "OG description missing", status: "fail", category: "opengraph", pts: 7 });
  }
  if (data.og_image) {
    score += 6;
    checks.push({ label: "OG image present", status: "pass", category: "opengraph", pts: 6 });
  } else {
    checks.push({ label: "OG image missing (affects social shares)", status: "fail", category: "opengraph", pts: 6 });
  }

  // 5. Twitter Card (12 pts: card 3, title 5, desc 4)
  if (data.twitter_card) {
    score += 3;
    checks.push({ label: "Twitter card type set", status: "pass", category: "twitter", pts: 3 });
  } else {
    checks.push({ label: "Twitter card type missing", status: "fail", category: "twitter", pts: 3 });
  }
  if (data.twitter_title) {
    score += 5;
    checks.push({ label: "Twitter title present", status: "pass", category: "twitter", pts: 5 });
  } else {
    checks.push({ label: "Twitter title missing", status: "fail", category: "twitter", pts: 5 });
  }
  if (data.twitter_description) {
    score += 4;
    checks.push({ label: "Twitter description present", status: "pass", category: "twitter", pts: 4 });
  } else {
    checks.push({ label: "Twitter description missing", status: "fail", category: "twitter", pts: 4 });
  }

  // 6. JSON-LD Schema (17 pts: present 12, valid JSON 5)
  const schema = data.schema_json || "";
  if (schema) {
    score += 12;
    checks.push({ label: "JSON-LD schema present", status: "pass", category: "schema", pts: 12 });
    try {
      JSON.parse(schema);
      score += 5;
      checks.push({ label: "Schema is valid JSON", status: "pass", category: "schema", pts: 5 });
    } catch {
      checks.push({ label: "Schema has invalid JSON syntax", status: "fail", category: "schema", pts: 5 });
    }
  } else {
    checks.push({ label: "JSON-LD schema missing (critical for rich results)", status: "fail", category: "schema", pts: 17 });
  }

  // 7. Canonical URL (8 pts)
  if (data.canonical_url) {
    score += 8;
    checks.push({ label: "Canonical URL set", status: "pass", category: "meta", pts: 8 });
  } else {
    checks.push({ label: "Canonical URL not set (auto-generated)", status: "warn", category: "meta", pts: 8 });
  }

  // 8. Robots (5 pts)
  if (data.robots) {
    score += 5;
    checks.push({ label: "Robots directive set", status: "pass", category: "meta", pts: 5 });
  } else {
    checks.push({ label: "Robots directive missing", status: "fail", category: "meta", pts: 5 });
  }

  const grade = score >= 90 ? "A" : score >= 75 ? "B" : score >= 55 ? "C" : score >= 35 ? "D" : "F";
  const gradeColor = { A: "#10b981", B: "#22d3ee", C: "#f59e0b", D: "#f97316", F: "#ef4444" }[grade];
  const gradeBg = { A: "bg-emerald-50 text-emerald-700 border-emerald-200", B: "bg-cyan-50 text-cyan-700 border-cyan-200", C: "bg-amber-50 text-amber-700 border-amber-200", D: "bg-orange-50 text-orange-700 border-orange-200", F: "bg-red-50 text-red-700 border-red-200" }[grade];

  return { score, grade, gradeColor, gradeBg, checks };
}

// ─── Build Status Badge ─────────────────────────────────────────
function formatRelative(ts) {
  if (!ts) return null;
  const diff = Math.max(0, (Date.now() / 1000) - ts);
  if (diff < 5) return "just now";
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function BuildStatusBadge({ lastBuiltAt }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 10_000);
    return () => clearInterval(id);
  }, []);
  if (!lastBuiltAt) return null;
  const rel = formatRelative(lastBuiltAt);
  const fresh = (Date.now() / 1000) - lastBuiltAt < 30;
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs flex-shrink-0 ${
        fresh ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-200" : "bg-white/5 border-white/15 text-slate-200"
      }`}
      data-testid="build-status-badge"
      title={`Built: ${new Date(lastBuiltAt * 1000).toLocaleString()}`}
    >
      <span className={`w-2 h-2 rounded-full ${fresh ? "bg-emerald-400 animate-pulse" : "bg-slate-400"}`} />
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-wide opacity-70">Static HTML</span>
        <span className="font-semibold">Built {rel}</span>
      </div>
    </div>
  );
}

// ─── Score Card Sub-Component ───────────────────────────────────
function SeoScoreCard({ score, grade, gradeColor, gradeBg, checks, pageName }) {
  const [expanded, setExpanded] = useState(false);
  const passed = checks.filter((c) => c.status === "pass").length;
  const warnings = checks.filter((c) => c.status === "warn").length;
  const failed = checks.filter((c) => c.status === "fail").length;

  const categories = [
    { id: "meta", label: "Meta Tags" },
    { id: "opengraph", label: "Open Graph" },
    { id: "twitter", label: "Twitter" },
    { id: "schema", label: "Schema" },
    { id: "keywords", label: "Keywords" },
  ];

  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="seo-score-card">
      <div className="p-4">
        <div className="flex items-center gap-4">
          {/* Grade Circle */}
          <div className="relative flex-shrink-0" data-testid="seo-grade-circle">
            <svg width="72" height="72" viewBox="0 0 72 72">
              <circle cx="36" cy="36" r="30" fill="none" stroke="#e2e8f0" strokeWidth="6" />
              <circle
                cx="36" cy="36" r="30" fill="none"
                stroke={gradeColor} strokeWidth="6"
                strokeDasharray={`${(score / 100) * 188.5} 188.5`}
                strokeLinecap="round"
                transform="rotate(-90 36 36)"
                style={{ transition: "stroke-dasharray 0.8s ease" }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold" style={{ color: gradeColor }} data-testid="seo-grade-letter">{grade}</span>
            </div>
          </div>

          {/* Score Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-heading font-semibold text-navy text-sm">SEO Score</h4>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${gradeBg}`} data-testid="seo-score-value">
                {score}/100
              </span>
            </div>
            {/* Progress Bar */}
            <div className="w-full bg-slate-100 rounded-full h-2 mb-2">
              <div
                className="h-2 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${score}%`, backgroundColor: gradeColor }}
                data-testid="seo-score-bar"
              />
            </div>
            {/* Summary Counts */}
            <div className="flex gap-3 text-xs">
              <span className="flex items-center gap-1 text-emerald-600">
                <CheckCircle className="w-3 h-3" /> {passed} passed
              </span>
              <span className="flex items-center gap-1 text-amber-600">
                <Info className="w-3 h-3" /> {warnings} warnings
              </span>
              <span className="flex items-center gap-1 text-red-500">
                <XCircle className="w-3 h-3" /> {failed} missing
              </span>
            </div>
          </div>

          {/* Expand Toggle */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex-shrink-0 p-2 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="seo-score-expand"
          >
            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0 border-t border-slate-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                {categories.map((cat) => {
                  const catChecks = checks.filter((c) => c.category === cat.id);
                  if (catChecks.length === 0) return null;
                  const catPassed = catChecks.filter((c) => c.status === "pass").length;
                  return (
                    <div key={cat.id} className="rounded-lg border border-slate-150 p-3 bg-slate-50/50" data-testid={`seo-score-${cat.id}`}>
                      <p className="text-xs font-semibold text-navy mb-2">{cat.label} ({catPassed}/{catChecks.length})</p>
                      <div className="space-y-1.5">
                        {catChecks.map((check, i) => (
                          <div key={i} className="flex items-start gap-1.5">
                            {check.status === "pass" ? (
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                            ) : check.status === "warn" ? (
                              <AlertCircle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                            )}
                            <span className={`text-xs leading-tight ${
                              check.status === "pass" ? "text-slate-500" : check.status === "warn" ? "text-amber-700" : "text-red-600"
                            }`}>
                              {check.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

// ─── Site Health Summary ────────────────────────────────────────
function SiteHealthSummary({ seoSettings }) {
  const globalSettings = seoSettings["global"] || {};
  const scores = seoPages.map((page) => {
    const isGlobal = page.slug === "global";
    return calculateSeoScore(seoSettings[page.slug] || {}, isGlobal ? null : globalSettings, isGlobal);
  });
  const avgScore = Math.round(scores.reduce((sum, s) => sum + s.score, 0) / scores.length);
  const avgGrade = avgScore >= 90 ? "A" : avgScore >= 75 ? "B" : avgScore >= 55 ? "C" : avgScore >= 35 ? "D" : "F";
  const avgColor = { A: "#10b981", B: "#22d3ee", C: "#f59e0b", D: "#f97316", F: "#ef4444" }[avgGrade];
  const gradeDistribution = { A: 0, B: 0, C: 0, D: 0, F: 0 };
  scores.forEach((s) => gradeDistribution[s.grade]++);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="site-health-summary">
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${avgColor}15` }}>
          <BarChart3 className="w-5 h-5" style={{ color: avgColor }} />
        </div>
        <div>
          <p className="text-xs text-slate-500">Site Average</p>
          <p className="text-lg font-bold text-navy">{avgScore}<span className="text-sm text-slate-400">/100</span></p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-emerald-500" />
        </div>
        <div>
          <p className="text-xs text-slate-500">Excellent (A)</p>
          <p className="text-lg font-bold text-navy">{gradeDistribution.A}<span className="text-sm text-slate-400">/{seoPages.length}</span></p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
          <AlertCircle className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <p className="text-xs text-slate-500">Needs Work</p>
          <p className="text-lg font-bold text-navy">{gradeDistribution.C + gradeDistribution.D}<span className="text-sm text-slate-400"> pages</span></p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
          <XCircle className="w-5 h-5 text-red-400" />
        </div>
        <div>
          <p className="text-xs text-slate-500">Critical (F)</p>
          <p className="text-lg font-bold text-navy">{gradeDistribution.F}<span className="text-sm text-slate-400"> pages</span></p>
        </div>
      </div>
    </div>
  );
}

export default function AdminSEO({ embedded = false }) {
  const [seoSettings, setSeoSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("global");
  const [saving, setSaving] = useState(false);
  const [lastBuiltAt, setLastBuiltAt] = useState(null);

  // Fetch build status on mount and after every save
  const refreshBuildStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo-ops/build-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d = await res.json();
        setLastBuiltAt(d.built_at || null);
      }
    } catch { /* ignore */ }
  };
  useEffect(() => {
    refreshBuildStatus();
    const id = setInterval(refreshBuildStatus, 30_000);
    return () => clearInterval(id);
  }, []);
  const [formData, setFormData] = useState(defaultSEO);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSection, setActiveSection] = useState("meta");
  const gSeo = seoSettings["global"] || {};

  const fetchSEOSettings = async () => {
    try {
      const data = await getAllSEOSettings();
      const settingsMap = {};
      data.forEach((s) => {
        settingsMap[s.page_slug] = s;
      });
      setSeoSettings(settingsMap);
      if (settingsMap[activeTab]) {
        setFormData(settingsMap[activeTab]);
      } else {
        setFormData({ ...defaultSEO, page_slug: activeTab });
      }
    } catch (error) {
      console.error("Error fetching SEO settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSEOSettings();
  }, []);

  useEffect(() => {
    if (seoSettings[activeTab]) {
      setFormData(seoSettings[activeTab]);
    } else {
      setFormData({ ...defaultSEO, page_slug: activeTab });
    }
    setHasChanges(false);
  }, [activeTab, seoSettings]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSEOSettings(activeTab, { ...formData, page_slug: activeTab });
      setHasChanges(false);
      fetchSEOSettings();

      // Auto-rebuild the static HTML for this page so view-source and
      // crawlers immediately see the new schema/meta/OG tags without the
      // admin needing to click a separate Rebuild or Deploy button.
      // On preview this hits the backend which runs node prerender.js.
      // On native production, prerender.js / node aren't in the backend
      // container — the endpoint returns 501 and we silently fall through
      // to a gentle reminder that deploy is needed.
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo-ops/regenerate-static-pages`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          toast.success(`Saved. ${data.ok} page${data.ok === 1 ? "" : "s"} updated — view-source reflects now.`);
          refreshBuildStatus();
        } else if (res.status === 501) {
          toast.success("Saved to database. Deploy to publish to production.");
        } else {
          toast.success("Saved.");
        }
      } catch {
        toast.success("Saved.");
      }
    } catch (error) {
      toast.error("Failed to save SEO settings");
    } finally {
      setSaving(false);
    }
  };

  const getStatusForPage = (slug) => {
    const settings = seoSettings[slug];
    if (!settings || !settings.meta_title) return "not-configured";
    return "configured";
  };

  // Calculate scores for all pages
  const pageScores = useMemo(() => {
    const globalSettings = seoSettings["global"] || {};
    const scores = {};
    seoPages.forEach((page) => {
      const isGlobal = page.slug === "global";
      scores[page.slug] = calculateSeoScore(
        seoSettings[page.slug] || {},
        isGlobal ? null : globalSettings,
        isGlobal
      );
    });
    return scores;
  }, [seoSettings]);

  // Active page score
  const activeScore = pageScores[activeTab] || { score: 0, grade: "F", gradeColor: "#ef4444", gradeBg: "bg-red-50 text-red-700 border-red-200", checks: [] };

  const sections = [
    { id: "meta", label: "Meta Tags", icon: Search },
    { id: "opengraph", label: "Open Graph", icon: Share2 },
    { id: "twitter", label: "Twitter Card", icon: Twitter },
    { id: "keywords", label: "Keywords & Competitors", icon: Tag },
  ];

  const content = (
      <div className="space-y-6" data-testid="admin-seo-page">
        {/* Header */}
        <div className="bg-navy rounded-xl p-6 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading font-bold text-2xl mb-2 flex items-center gap-2">
                <Globe className="w-6 h-6" />
                SEO Management Center
              </h2>
              <p className="text-slate-300 font-body">
                Manage meta tags for every public page. Open Graph and Twitter cards copy the meta tags, keywords and competitors come from Global Defaults, and structured data (schema) is generated automatically.
              </p>
            </div>
            <BuildStatusBadge lastBuiltAt={lastBuiltAt} />
          </div>
        </div>

        {/* Site Health Summary */}
        {!loading && <SiteHealthSummary seoSettings={seoSettings} />}

        <SeoHealthCheck />

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Pages List */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-navy text-sm">Pages</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <div className="space-y-1">
                  {seoPages.map((page) => {
                    const ps = pageScores[page.slug];
                    return (
                      <button
                        key={page.slug}
                        onClick={() => setActiveTab(page.slug)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                          activeTab === page.slug
                            ? "bg-teal text-white"
                            : "hover:bg-slate-50"
                        }`}
                        data-testid={`seo-tab-${page.slug}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className={`font-body text-sm font-medium ${
                            activeTab === page.slug ? "text-white" : "text-navy"
                          }`}>
                            {page.name}
                          </p>
                          {page.slug === "global" && (
                            <p className={`text-xs mt-0.5 ${activeTab === page.slug ? "text-white/70" : "text-slate-400"}`}>
                              Applies to all pages
                            </p>
                          )}
                        </div>
                        {ps && (
                          <span
                            className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${
                              activeTab === page.slug
                                ? "bg-white/20 text-white border-white/30"
                                : ps.gradeBg
                            }`}
                            data-testid={`seo-grade-${page.slug}`}
                          >
                            {ps.grade}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* SERP Preview */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-navy text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Google Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-1" data-testid="serp-preview">
                  <p className="text-blue-700 text-lg font-body truncate" data-testid="serp-preview-title">
                    {formData.meta_title || "Page Title"}
                  </p>
                  <p className="text-green-700 text-sm font-body truncate">
                    {formData.canonical_url || `primemediawireless.com/${activeTab === "home" ? "" : activeTab}`}
                  </p>
                  <p className="text-slate-600 text-sm font-body line-clamp-2" data-testid="serp-preview-desc">
                    {formData.meta_description || "Meta description will appear here..."}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* SEO Form */}
          <div className="lg:col-span-3 space-y-4">
            {/* Per-page Score Card */}
            {!loading && (
              <SeoScoreCard
                {...activeScore}
                pageName={seoPages.find((p) => p.slug === activeTab)?.name}
              />
            )}

            {loading ? (
              <Card className="border-slate-200">
                <CardContent className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal mx-auto"></div>
                  <p className="text-slate-500 mt-4 font-body">Loading SEO settings...</p>
                </CardContent>
              </Card>
            ) : (
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Card className="border-slate-200">
                  <CardHeader className="border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-heading text-navy">
                          {seoPages.find((p) => p.slug === activeTab)?.name} SEO
                        </CardTitle>
                        <p className="text-sm text-slate-500 font-body mt-1">
                          {seoPages.find((p) => p.slug === activeTab)?.description}
                        </p>
                      </div>
                      {hasChanges && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded font-body" data-testid="unsaved-badge">
                          Unsaved changes
                        </span>
                      )}
                    </div>
                    {/* Section Tabs */}
                    <div className="flex flex-wrap gap-1 mt-4">
                      {sections.map((sec) => (
                        <button
                          key={sec.id}
                          onClick={() => setActiveSection(sec.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-body transition-colors ${
                            activeSection === sec.id
                              ? "bg-navy text-white"
                              : "text-slate-600 hover:bg-slate-100"
                          }`}
                          data-testid={`seo-section-${sec.id}`}
                        >
                          <sec.icon className="w-3.5 h-3.5" />
                          {sec.label}
                        </button>
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">

                    {/* META TAGS SECTION */}
                    {activeSection === "meta" && (
                      <div className="space-y-4" data-testid="seo-meta-section">
                        <h3 className="font-heading font-semibold text-navy flex items-center gap-2">
                          <Search className="w-4 h-4" />
                          Basic Meta Tags
                        </h3>
                        <div>
                          <Label htmlFor="meta_title">Meta Title</Label>
                          <Input
                            id="meta_title"
                            value={formData.meta_title || ""}
                            onChange={(e) => handleChange("meta_title", e.target.value)}
                            placeholder="Page Title | Prime Media Wireless"
                            data-testid="seo-meta-title"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            {(formData.meta_title || "").length}/60 characters (recommended: 50-60)
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="meta_description">Meta Description</Label>
                          <Textarea
                            id="meta_description"
                            value={formData.meta_description || ""}
                            onChange={(e) => handleChange("meta_description", e.target.value)}
                            placeholder="Describe this page in 150-160 characters..."
                            rows={3}
                            data-testid="seo-meta-description"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            {(formData.meta_description || "").length}/160 characters (recommended: 150-160)
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="robots">Robots Directive</Label>
                          <Input
                            id="robots"
                            value={formData.robots || "index, follow"}
                            onChange={(e) => handleChange("robots", e.target.value)}
                            placeholder="index, follow"
                            data-testid="seo-robots"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Common: "index, follow" | "noindex, nofollow" | "index, follow, max-snippet:-1, max-image-preview:large"
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="canonical_url">Canonical URL</Label>
                          <Input
                            id="canonical_url"
                            value={formData.canonical_url || ""}
                            onChange={(e) => handleChange("canonical_url", e.target.value)}
                            placeholder="https://primemediawireless.com/page"
                            data-testid="seo-canonical-url"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Leave empty to auto-generate from current URL
                          </p>
                        </div>
                        {activeTab === "global" && (
                          <div>
                            <Label htmlFor="site_name">Site Name</Label>
                            <Input
                              id="site_name"
                              value={formData.site_name || ""}
                              onChange={(e) => handleChange("site_name", e.target.value)}
                              placeholder="Prime Media Wireless"
                              data-testid="seo-site-name"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              Used in OG tags and schema as the site-wide brand name
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* OPEN GRAPH SECTION */}
                    {activeSection === "opengraph" && (
                      <div className="space-y-4" data-testid="seo-og-section">
                        <h3 className="font-heading font-semibold text-navy flex items-center gap-2">
                          <Share2 className="w-4 h-4" />
                          Open Graph (Social Sharing)
                        </h3>
                        <p className="text-sm text-slate-500 font-body">
                          Controls how links appear when shared on Facebook, LinkedIn, and other platforms.
                        </p>
                        <div>
                          <Label htmlFor="og_title">OG Title</Label>
                          <Input
                            id="og_title"
                            value={formData.meta_title || gSeo.meta_title || ""}
                            disabled
                            data-testid="seo-og-title"
                          />
                          <p className="text-xs text-teal mt-1">Copied automatically from Meta Tags.</p>
                        </div>
                        <div>
                          <Label htmlFor="og_description">OG Description</Label>
                          <Textarea
                            id="og_description"
                            value={formData.meta_description || gSeo.meta_description || ""}
                            disabled
                            rows={2}
                            data-testid="seo-og-description"
                          />
                          <p className="text-xs text-teal mt-1">Copied automatically from Meta Tags.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="og_type">OG Type</Label>
                            <Input
                              id="og_type"
                              value={formData.og_type || ""}
                              onChange={(e) => handleChange("og_type", e.target.value)}
                              placeholder="website"
                              data-testid="seo-og-type"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              Common: website, article, product
                            </p>
                          </div>
                          <div>
                            <Label htmlFor="og_url">OG URL</Label>
                            <Input
                              id="og_url"
                              value={formData.og_url || ""}
                              onChange={(e) => handleChange("og_url", e.target.value)}
                              placeholder="https://primemediawireless.com"
                              data-testid="seo-og-url"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="og_image">OG Image URL</Label>
                          <Input
                            id="og_image"
                            value={formData.og_image || ""}
                            onChange={(e) => handleChange("og_image", e.target.value)}
                            placeholder="https://example.com/og-image.jpg"
                            data-testid="seo-og-image"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Recommended: 1200x630 pixels. This image shows when links are shared on social media.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* TWITTER CARD SECTION */}
                    {activeSection === "twitter" && (
                      <div className="space-y-4" data-testid="seo-twitter-section">
                        <h3 className="font-heading font-semibold text-navy flex items-center gap-2">
                          <Twitter className="w-4 h-4" />
                          Twitter Card
                        </h3>
                        <p className="text-sm text-slate-500 font-body">
                          Controls how links appear when shared on Twitter/X. Title, description and image are copied automatically.
                        </p>
                        <div>
                          <Label htmlFor="twitter_card">Card Type</Label>
                          <Input
                            id="twitter_card"
                            value={formData.twitter_card || ""}
                            onChange={(e) => handleChange("twitter_card", e.target.value)}
                            placeholder="summary_large_image"
                            data-testid="seo-twitter-card"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Options: summary, summary_large_image, app, player
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="twitter_title">Twitter Title</Label>
                          <Input
                            id="twitter_title"
                            value={formData.meta_title || gSeo.meta_title || ""}
                            disabled
                            data-testid="seo-twitter-title"
                          />
                          <p className="text-xs text-teal mt-1">Copied automatically from Meta Tags.</p>
                        </div>
                        <div>
                          <Label htmlFor="twitter_description">Twitter Description</Label>
                          <Textarea
                            id="twitter_description"
                            value={formData.meta_description || gSeo.meta_description || ""}
                            disabled
                            rows={2}
                            data-testid="seo-twitter-description"
                          />
                          <p className="text-xs text-teal mt-1">Copied automatically from Meta Tags.</p>
                        </div>
                        <div>
                          <Label htmlFor="twitter_image">Twitter Image URL</Label>
                          <Input
                            id="twitter_image"
                            value={formData.og_image || gSeo.og_image || ""}
                            disabled
                            data-testid="seo-twitter-image"
                          />
                          <p className="text-xs text-teal mt-1">Copied automatically from the OG image.</p>
                        </div>
                      </div>
                    )}

                    {/* KEYWORDS & COMPETITORS SECTION */}
                    {activeSection === "keywords" && (
                      <div className="space-y-4" data-testid="seo-keywords-section">
                        <h3 className="font-heading font-semibold text-navy flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          Keywords & Competitor Targeting
                        </h3>
                        {activeTab !== "global" ? (
                          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-2" data-testid="seo-keywords-global-note">
                            <p className="text-sm text-slate-600">Keywords and competitors for every page are set in <b>Global Defaults</b>.</p>
                            <div className="flex flex-wrap gap-1.5">
                              {[...new Set([...(gSeo.meta_keywords || "").split(","), ...(gSeo.competitor_keywords || "").split(",")]
                                .map((k) => k.trim()).filter(Boolean))]
                                .map((kw, i) => <span key={i} className="px-2 py-0.5 bg-white border border-slate-200 text-xs rounded-full text-slate-700">{kw}</span>)}
                            </div>
                            <Button variant="outline" size="sm" onClick={() => setActiveTab("global")} data-testid="seo-go-global-keywords">Edit in Global Defaults</Button>
                          </div>
                        ) : (<>
                        <p className="text-sm text-slate-500 font-body">
                          These keywords and competitors apply to every page on the site.
                        </p>
                        <div>
                          <Label htmlFor="meta_keywords">Meta Keywords</Label>
                          <Textarea
                            id="meta_keywords"
                            value={formData.meta_keywords || ""}
                            onChange={(e) => handleChange("meta_keywords", e.target.value)}
                            placeholder="Total Wireless, wireless dealer, Verizon prepaid, master agent"
                            rows={3}
                            data-testid="seo-meta-keywords"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Comma-separated keywords used on every page
                          </p>
                        </div>
                        <div>
                          <Label htmlFor="competitor_keywords">Competitor & Brand Keywords</Label>
                          <Textarea
                            id="competitor_keywords"
                            value={formData.competitor_keywords || ""}
                            onChange={(e) => handleChange("competitor_keywords", e.target.value)}
                            placeholder="Antgen, Victra, Pinnacle Mobility, TCetra, VidaPay, Paramount Wireless, ADN Wireless"
                            rows={4}
                            data-testid="seo-competitor-keywords"
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            Competitor brand names and alternative search terms (comma-separated). These are merged with meta keywords in the rendered page.
                          </p>
                        </div>
                        {(formData.meta_keywords || formData.competitor_keywords) && (
                          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200" data-testid="keywords-preview">
                            <p className="text-xs font-semibold text-navy mb-2">Combined Keywords Preview:</p>
                            <div className="flex flex-wrap gap-1.5">
                              {[...new Set([...(formData.meta_keywords || "").split(","), ...(formData.competitor_keywords || "").split(",")]
                                .map(k => k.trim())
                                .filter(Boolean))]
                                .map((kw, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-white border border-slate-200 text-xs rounded-full text-slate-700">
                                    {kw}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                        <div className="pt-2" data-testid="seo-keyword-templates"><AdminKeywordTemplates embedded /></div>
                        </>)}
                      </div>
                    )}

                    {/* Save Button */}
                    <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                      <Button
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        className="bg-teal hover:bg-teal-dark text-white"
                        data-testid="save-seo-btn"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save SEO Settings
                          </>
                        )}
                      </Button>
                      {activeTab !== "global" && (
                        <p className="text-xs text-slate-400 font-body">
                          Empty fields inherit from Global Defaults
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </div>
  );

  if (embedded) return content;
  return <AdminLayout title="SEO Settings" breadcrumb="SEO">{content}</AdminLayout>;
}
